
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@/contexts/auth";
import { useToast } from "@/components/ui/use-toast";
import { supabase, getUserOrganizations } from "@/lib/supabase";
import { Organization } from "@/types/subscription";

export type PendingInvitation = {
  id: string;
  email: string;
  status: string;
  organization_id: string;
};

export function useUserInvitation() {
  const { token } = useParams();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [emailList, setEmailList] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string>("");
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [remainingInvites, setRemainingInvites] = useState<number>(0);
  const [pendingInvites, setPendingInvites] = useState<PendingInvitation[]>([]);
  const [activePage, setActivePage] = useState("team");

  useEffect(() => {
    const fetchUserOrganizations = async () => {
      if (!user) return;

      try {
        const { success, data, error } = await getUserOrganizations(user.id);

        if (!success || error) {
          throw new Error(error || "Erreur lors de la récupération des organisations");
        }
        
        if (data && data.length > 0) {
          setOrganizations(data);
          setSelectedOrgId(data[0].id);
        } else {
          console.log("Aucune organisation trouvée pour cet utilisateur");
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des organisations:", error);
        toast({
          title: "Erreur",
          description: "Impossible de charger vos organisations.",
          variant: "destructive",
        });
      }
    };

    fetchUserOrganizations();
  }, [user, toast]);

  useEffect(() => {
    const fetchOrganizationDetails = async () => {
      if (!selectedOrgId) return;

      setIsLoading(true);
      try {
        const selectedOrg = organizations.find(org => org.id === selectedOrgId);
        if (selectedOrg) {
          setSelectedOrg(selectedOrg);
          
          const remaining = selectedOrg.max_users - selectedOrg.current_users;
          setRemainingInvites(remaining > 0 ? remaining : 0);
        }

        const { data: invitesData, error: invitesError } = await supabase
          .from('organization_invitations')
          .select('*')
          .eq('organization_id', selectedOrgId)
          .eq('status', 'pending');

        if (invitesError) throw invitesError;
        
        if (invitesData) {
          setPendingInvites(invitesData);
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des détails:", error);
        toast({
          title: "Erreur",
          description: "Impossible de charger les informations de l'organisation.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrganizationDetails();
  }, [selectedOrgId, organizations, toast]);

  const handleSubmitInvitations = async () => {
    if (!selectedOrgId) {
      toast({
        title: "Aucune organisation sélectionnée",
        description: "Veuillez sélectionner une organisation avant d'envoyer des invitations.",
        variant: "destructive",
      });
      return;
    }

    if (emailList.length === 0) {
      toast({
        title: "Aucun e-mail ajouté",
        description: "Veuillez ajouter au moins une adresse e-mail pour envoyer des invitations.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const invitations = emailList.map(email => ({
        email,
        organization_id: selectedOrgId,
        invited_by: user?.id,
        status: 'pending',
        role: 'member',
        created_at: new Date().toISOString()
      }));

      const { data, error } = await supabase
        .from('organization_invitations')
        .insert(invitations)
        .select();

      if (error) throw error;

      if (data) {
        setPendingInvites([...pendingInvites, ...data]);
      }

      setEmailList([]);

      toast({
        title: "Invitations enregistrées",
        description: `${invitations.length} invitation(s) ont été enregistrées avec succès.`,
        variant: "default",
      });
    } catch (error) {
      console.error("Erreur lors de l'envoi des invitations:", error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de l'envoi des invitations.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    token,
    user,
    emailList,
    setEmailList,
    isLoading,
    organizations,
    selectedOrgId,
    setSelectedOrgId,
    selectedOrg,
    remainingInvites,
    pendingInvites,
    activePage,
    setActivePage,
    handleSubmitInvitations
  };
}
